/**
 * Referrals (Backlog 061 step 1): who referred this user, how many accounts registered with their code against
 * the 50 bar, and those accounts. Read-only, and deliberately money-free: the earnings ledger does not exist yet.
 */

import { useState } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import { Box, Chip, LinearProgress, Link, Paper, Typography } from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import { DataTable, type DataTableColumn } from '@components/common/DataTable'
import { storiesApi } from '@api/endpoints/stories'
import type { ReferralUserDto } from '@api/types/stories'
import { errorMessage, formatDay } from './userFormatting'

export function UserReferralsPanel({ userId }: { userId: string }) {
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(25)

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin-user-referrals', userId, page, pageSize],
    queryFn: () => storiesApi.getUserReferrals(userId, page + 1, pageSize),
  })

  const columns: DataTableColumn<ReferralUserDto>[] = [
    {
      key: 'user',
      header: 'Referred user',
      primary: true,
      render: (u) => (
        <Link component={RouterLink} to={`/users/${u.id}`} underline="hover" sx={{ wordBreak: 'break-word' }}>
          {u.displayName || u.email || u.id.slice(0, 8)}
        </Link>
      ),
    },
    { key: 'email', header: 'Email', hideBelow: 'md', render: (u) => u.email || '—' },
    {
      key: 'status',
      header: 'Status',
      render: (u) => <Chip size="small" variant="outlined" color={u.isActive ? 'success' : 'default'} label={u.isActive ? 'Active' : 'Inactive'} />,
    },
    { key: 'joined', header: 'Registered', render: (u) => formatDay(u.createdDate) },
  ]

  const count = data?.count ?? 0
  const threshold = data?.threshold ?? 50

  return (
    <Box>
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
          <Typography variant="h6" component="div">
            {count} of {threshold}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            registered referrals{count >= threshold ? ' (bar reached)' : ''}
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={Math.min(100, (count / Math.max(1, threshold)) * 100)}
          sx={{ my: 1, height: 6, borderRadius: 3 }}
        />
        {data?.referralCode && (
          <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
            Referral code: <strong>{data.referralCode}</strong>
          </Typography>
        )}
        <Typography variant="body2" sx={{ mt: 1 }}>
          Referred by:{' '}
          {data?.referredBy ? (
            <Link component={RouterLink} to={`/users/${data.referredBy.id}`} underline="hover">
              {data.referredBy.displayName || data.referredBy.email || data.referredBy.id.slice(0, 8)}
            </Link>
          ) : (
            <Typography component="span" variant="body2" color="text.secondary">
              nobody
            </Typography>
          )}
        </Typography>
      </Paper>

      <DataTable
        rows={data?.items ?? []}
        columns={columns}
        getRowKey={(u) => u.id}
        loading={isLoading}
        error={error ? errorMessage(error, 'Failed to load referrals') : null}
        emptyMessage="No accounts registered with this user's code"
        dense
        page={page}
        pageSize={pageSize}
        total={count}
        onPageChange={setPage}
        onPageSizeChange={(size) => {
          setPageSize(size)
          setPage(0)
        }}
        pageSizeOptions={[10, 25, 50, 100]}
      />
    </Box>
  )
}
